import React, { useCallback, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { AuroraBackdrop } from '../AuroraBackdrop';
import { SupabaseSetupNotice } from '../SupabaseSetupNotice';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { StudentDetailModal } from '../students/StudentDetailModal';
import { StudentFormModal } from '../students/StudentFormModal';
import { useAuth } from '../../contexts/AuthContext';
import { StudentsProvider, useStudents } from '../../contexts/StudentsContext';
import { useIdleLock } from '../../hooks/useIdleLock';
function ShellBody() {
  const {
    students,
    error,
    setupRequired,
    openCreateForm
  } = useStudents();
  const {
    lock
  } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Shared school computers: lock back to the master password when idle.
  const handleIdle = useCallback(() => {
    lock();
    toast.info('Locked after 15 minutes of inactivity.');
  }, [lock]);
  useIdleLock(handleIdle);
  return <div className="min-h-screen w-full" data-id="element-149" data-mp="1fgcrqhua50g4">
      <AuroraBackdrop data-id="element-150" data-mp="1fhe54syrpz10" />

      <Sidebar collapsed={collapsed} onToggleCollapsed={() => setCollapsed(value => !value)} mobileOpen={mobileOpen} onCloseMobile={() => setMobileOpen(false)} totalStudents={students.length} data-id="element-151" data-mp="1fei6t3j0d8c1" />

      <Topbar onOpenMobileNav={() => setMobileOpen(true)} sidebarCollapsed={collapsed} data-id="element-152" data-mp="235nnfkwrw4ne" />

      <main className={`px-4 pb-28 pt-6 transition-[margin] duration-300 sm:px-6 lg:pb-12 ${collapsed ? 'lg:ml-[84px]' : 'lg:ml-[264px]'}`} data-id="element-153" data-mp="1y6u77kq3pzjj">
        {setupRequired ? <div className="mx-auto mb-6 w-full max-w-3xl" data-id="element-154" data-mp="msgnms2ds69b">
            <SupabaseSetupNotice data-id="element-155" data-mp="mpuguunbdus5" />
          </div> : error && <div role="alert" className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4" data-id="element-156" data-mp="hrj4od9jr86r">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" data-id="element-157" data-mp="39zd1xhzsmwpm" />
              <div className="min-w-0 text-sm" data-id="element-158" data-mp="2w14ziujzn372">
                <p className="font-semibold text-foreground" data-id="element-159" data-mp="2w0d8zy58zvnn">Your records could not be loaded</p>
                <p className="mt-0.5 break-words text-muted-foreground" data-id="element-160" data-mp="30xvdncc8fopu">{error}</p>
              </div>
            </div>}

        <Outlet data-id="element-161" data-mp="2r1a7uose29ky" />
      </main>

      {/* Floating quick action */}
      <motion.button type="button" onClick={openCreateForm} initial={{
      opacity: 0,
      scale: 0.8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: [0, -6, 0]
    }} transition={{
      opacity: {
        duration: 0.3
      },
      scale: {
        duration: 0.3
      },
      y: {
        duration: 4.5,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }} whileHover={{
      scale: 1.06
    }} whileTap={{
      scale: 0.96
    }} aria-label="Add student" className="fixed bottom-6 right-5 z-30 inline-flex h-14 items-center gap-2 rounded-2xl brand-gradient px-5 text-sm font-semibold text-white shadow-2xl shadow-indigo-500/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:bottom-8 sm:right-8" data-id="element-162" data-mp="2r3myrrg4jua5">
        <Plus className="h-5 w-5" data-id="element-163" data-mp="1tqoldigq40w8" />
        <span className="hidden sm:inline" data-id="element-164" data-mp="27nbl1t02955u">Add Student</span>
      </motion.button>

      <StudentFormModal data-id="element-165" data-mp="23mrlkis0lpgj" />
      <StudentDetailModal data-id="element-166" data-mp="23lglou1ox1up" />
    </div>;
}
export function DashboardShell() {
  return <StudentsProvider data-id="element-167" data-mp="2he6992wvaetx">
      <ShellBody data-id="element-168" data-mp="2hden0ip5ttgb" />
    </StudentsProvider>;
}